import { useState } from "react";
import { useDeleteAdminUserMutation } from "@/api/api";
import { getApiErrorDetail } from "@/api/errors";
import { HGAdminUser } from "@/common/types";
import { globalReloader } from "@/providers/dbRecordsLoader";
import { Button } from "../button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../dialog";
import { Field, FieldLabel } from "../field";
import { Input } from "../input";

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

export default function AdminUserDeleteDialog({
  open,
  onOpenChange,
  user,
  onError,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: HGAdminUser;
  onError: (message: string | null) => void;
}) {
  const [deleteUser, { isLoading }] = useDeleteAdminUserMutation();
  const [confirmation, setConfirmation] = useState("");

  // The deletion takes every collection and record with it and cannot be
  // undone, so the admin types the account's handle rather than just
  // clicking through.
  const handle = user.nickname || user.username || user.id;
  const confirmed = confirmation.trim() === handle;

  const handleOpenChange = (next: boolean) => {
    if (!next) setConfirmation("");
    onOpenChange(next);
  };

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!confirmed) return;
    onError(null);
    try {
      await deleteUser(user.id).unwrap();
      // The timeline's loader is outside RTK Query and may still hold this
      // account's records in its cache.
      globalReloader.reload();
    } catch (e) {
      onError(
        getApiErrorDetail(
          e,
          "Could not delete that account. Please try again.",
        ),
      );
    }
    handleOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        {/* Inside DialogContent, not around it -- see AdminPasswordDialog. */}
        <form onSubmit={handleDelete}>
          <DialogHeader>
            <DialogTitle>Delete account</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <p>
              This permanently deletes
              <span className="font-semibold">{" " + handle}</span>
              {`, ${plural(user.collections_count, "collection")} and ${plural(user.records_count, "record")}`}
              , including uploaded images. External connections are revoked.
              This cannot be undone.
            </p>
            <Field>
              <FieldLabel htmlFor="adminDeleteConfirm">
                Type <span className="font-mono">{handle}</span> to confirm
              </FieldLabel>
              <Input
                id="adminDeleteConfirm"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                autoComplete="off"
              />
            </Field>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              variant="destructive"
              disabled={isLoading || !confirmed}
            >
              Delete
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
